package com.spground.spaceplaygroundwallpaper

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.events.MouseEvent

/**
 * Web host for the scene engine — plays the role SpaceWallpaperService plays on
 * Android: owns the surface, the timing loop, and the parallax offset.
 * Pointer position stands in for the home-screen page swipe; click = warp
 * (Android's triple-tap).
 */
fun main() {
    Assets.load { start() }
}

private fun start() {
    val canvasEl = document.getElementById("scene") as HTMLCanvasElement
    val ctx = canvasEl.getContext("2d") as CanvasRenderingContext2D

    // Cap DPR at 2: past that the fill cost explodes with no visible gain.
    val dpr = window.devicePixelRatio.coerceAtMost(2.0)
    val engine = SpaceSceneEngine(density = dpr.toFloat())

    SettingsPanel(engine).install()

    var canvas = Canvas(ctx, canvasEl.width, canvasEl.height)
    var screenW = 0f
    var screenH = 0f

    fun resize() {
        val w = (window.innerWidth * dpr).toInt()
        val h = (window.innerHeight * dpr).toInt()
        if (w == canvasEl.width && h == canvasEl.height) return
        canvasEl.width = w
        canvasEl.height = h
        screenW = w.toFloat()
        screenH = h.toFloat()
        canvas = Canvas(ctx, w, h)
        engine.setSize(screenW, screenH)
    }
    resize()
    window.addEventListener("resize", { resize() })

    // Parallax: pointer position maps to a viewport shift, eased toward its
    // target so it feels like the wallpaper's page-swipe glide. The engine's
    // spawn margins assume shifts up to 14% of the screen — same clamp as the
    // Android service.
    var targetOffsetX = 0f
    var targetOffsetY = 0f
    var offsetX = 0f
    var offsetY = 0f
    window.addEventListener("mousemove", { e ->
        e as MouseEvent
        val amount = WallpaperSettings.getInt(
            WallpaperSettings.KEY_PARALLAX_AMOUNT, WallpaperSettings.DEF_PARALLAX_AMOUNT
        ) / 100f
        val maxShiftX = screenW * 0.14f * amount
        val maxShiftY = screenH * 0.14f * amount
        val nx = (e.clientX / window.innerWidth.toDouble() - 0.5).toFloat() * 2f
        val ny = (e.clientY / window.innerHeight.toDouble() - 0.5).toFloat() * 2f
        targetOffsetX = -nx * maxShiftX
        targetOffsetY = -ny * maxShiftY
    })

    // Click = warp, like the wallpaper's triple-tap. 'r' rebuilds the scene.
    window.addEventListener("click", { engine.triggerWarp() })
    window.addEventListener("keydown", { e ->
        if (e.asDynamic().key == "r") engine.reset()
    })

    var lastT = 0.0
    fun frame(t: Double) {
        // Clamp dt so a background tab doesn't fast-forward physics on return.
        val dt = if (lastT == 0.0) 0f else ((t - lastT) / 1000.0).coerceIn(0.0, 0.05).toFloat()
        lastT = t

        val ease = (dt * 4f).coerceAtMost(1f)
        offsetX += (targetOffsetX - offsetX) * ease
        offsetY += (targetOffsetY - offsetY) * ease
        engine.setViewportOffset(offsetX, offsetY)

        engine.updateAndDraw(canvas, dt)
        window.requestAnimationFrame(::frame)
    }
    window.requestAnimationFrame(::frame)
}
